import {FieldError, Input, Label, TextField} from 'react-aria-components'

import ChevronDownSvg from '@/assets/icons/chevron-down.svg?react'
import InfoSvg from '@/assets/icons/info.svg?react'
import PriceHistorySvg from '@/assets/icons/price-history.svg?react'
import stripeTrustBadgePng from '@/assets/images/stripe-trust-badge.png'
import useIsAuthenticated from '@/auth/hooks/useIsAuthenticated'
import Price from '@/components/Price'
import PrimaryButton from '@/components/PrimaryButton/PrimaryButton'
import Switch from '@/components/Switch/Switch'
import {USD_DISPLAY_DECIMALS} from '@/constants/config'
import useCartSummary from '@/lib/cart/hooks/useCartSummary'
import ExpressCheckout from '@/lib/payment/components/ExpressCheckout'
import {useRoundUp} from '@/lib/payment/store'
import {CartRoute, PaymentRoute} from '@/routeRegistry'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import {useIsMobile} from '@/utils/hooks/useWindowSize'
import useSearch from '@/utils/router/useSearch'

import style from './Cart.module.scss'

export default memo(function CartSummary() {
  // const router = useRouter()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const [isOpen, setIsOpen] = useState(!isMobile)
  // const [isCheckingOut, setIsCheckingOut] = useState(false)
  // const [locale] = useLocale()
  // const queryClient = useQueryClient()
  const isAuthenticated = useIsAuthenticated()
  const [isRewardsApplied, setIsRewardsApplied] = useSearch(CartRoute.id, 'applyRewards')
  const latestIsRewardsApplied = useLatest(isRewardsApplied)
  const [coupon, setCoupon] = useSearch(CartRoute.id, 'coupon')
  const [roundUp, setRoundUp] = useRoundUp()

  const {
    cashback,
    cartItemNumber,
    total,
    discountValue,
    derivedDiscountValue,
    subtotal,
    applyCoupon,
    couponError,
    handleCouponChange,
    couponInfo,
    isCouponLoading,
    roundUpAmount,
    totalAfterTax,
  } = useCartSummary(isRewardsApplied, coupon, setCoupon)

  const latestCoupon = useLatest(coupon)

  const isCouponValid = couponError === null

  // const successHref = useMemo(
  //   () =>
  //     router.buildLocation({
  //       to: CheckoutRoute.fullPath,
  //       params: {
  //         result: CheckoutResult.Success,
  //       },
  //       search: {
  //         guest: isAuthenticated ? true : undefined,
  //       },
  //     }).href,
  //   [router],
  // )

  // const failureHref = useMemo(
  //   () =>
  //     router.buildLocation({
  //       to: CartRoute.fullPath,
  //       search: {
  //         cancel: true,
  //       },
  //     }).href,
  //   [router],
  // )

  const onPress = useCallback(() => {
    void navigate({
      to: PaymentRoute.fullPath,
      search: {
        applyRewards: latestIsRewardsApplied.current,
        coupon: latestCoupon.current,
      },
    })

    // NOTE: below is the old flow
    // logEvent(...CHECKOUT_EVENT)
    // setIsCheckingOut(true)
    // announce('Checking out')

    // checkout({
    //   locale,
    //   successHref,
    //   failureHref,
    //   queryClient,
    //   applyRewards: latestIsRewardsApplied.current,
    // })
    //   .catch(error => {
    //     if (isCartCalibratedError(error)) {
    //       toastErrorMessage(buildStringForCalibratedError(error))
    //     } else {
    //       toastErrorMessage(error)
    //     }
    //   })
    //   .finally(() => {
    //     setIsCheckingOut(false)
    //   })
    //   .catch(() => {
    //     /* empty */
    //   })
  }, [navigate])

  return (
    <div className={style.CartSummary}>
      <h2 className={style.Title}>
        Order summary{' '}
        <button
          className={style.ShowHide}
          aria-label='Order summary'
          aria-expanded={isOpen}
          onClick={() => {
            setIsOpen(isOpen => !isOpen)
          }}
        >
          <ChevronDownSvg className={style.Decoration} aria-hidden />
        </button>
      </h2>
      {isOpen && (
        <>
          <div className={style.SumCategory}>
            <span className={style.SumCategoryName}>
              Subtotal ({cartItemNumber} item{cartItemNumber > 1 && 's'})
            </span>
            <span className={style.SumCategoryValue}>
              <Price
                price={subtotal.toNumber()}
                options={{
                  fractionDigits: USD_DISPLAY_DECIMALS,
                  exactFractionDigits: true,
                }}
              />
            </span>
          </div>
          <div className={style.SumCategory}>
            <span className={clsx(style.SumCategoryName, style.Strong)}>
              Taxes calculated at checkout
            </span>
          </div>
          {roundUpAmount.gt(0) && (
            <>
              <div className={style.SumCategory}>
                <span className={clsx(style.SumCategoryName)}>Total after tax</span>
                <span className={clsx(style.SumCategoryValue)}>
                  <Price
                    price={totalAfterTax}
                    options={{
                      fractionDigits: USD_DISPLAY_DECIMALS,
                      exactFractionDigits: true,
                    }}
                  />
                </span>
              </div>
              <div className={clsx(style.SumCategory, style.Secondary)}>
                <div className={style.SwitchWrapper}>
                  <Switch
                    label={<>Donate change to NFB</>}
                    aria-label='Donate change to NFB'
                    onChange={setRoundUp}
                    isSelected={roundUp}
                    isLabelVisible
                    labelClassName={style.SumCategoryName}
                  />
                  <a
                    href='https://nfb.org/about-us/press-room/innosearch-ai-and-national-federation-blind-announce-strategic-partnership'
                    className={style.Info}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <InfoSvg aria-label='Read more about NFB change donation program' />
                  </a>
                </div>
                <span
                  className={clsx(
                    style.SumCategoryValue,
                    roundUp ? style.RoundedUp : style.UnRounded,
                  )}
                >
                  <Price
                    price={roundUpAmount}
                    options={{
                      fractionDigits: USD_DISPLAY_DECIMALS,
                      exactFractionDigits: true,
                    }}
                  />
                </span>
              </div>
            </>
          )}
          {isAuthenticated && discountValue > 0 && (
            <div className={style.SumCategory}>
              <Switch
                label='Apply rewards'
                aria-label='Apply rewards'
                onChange={setIsRewardsApplied}
                isSelected={isRewardsApplied}
                isLabelVisible
                labelClassName={style.SumCategoryName}
              />
              {isRewardsApplied && (
                <span
                  className={clsx(
                    style.SumCategoryValue,
                    derivedDiscountValue > 0 && style.GoodValue,
                  )}
                >
                  <Price
                    price={-derivedDiscountValue}
                    options={{
                      fractionDigits: USD_DISPLAY_DECIMALS,
                      exactFractionDigits: true,
                    }}
                  />
                </span>
              )}
            </div>
          )}
          <VisuallyHidden
            as='form'
            className={style.CouponForm}
            onSubmit={e => {
              e.preventDefault()
              applyCoupon()
            }}
            isVisible={!isRewardsApplied}
          >
            <TextField
              name='coupon'
              onChange={handleCouponChange}
              validationBehavior='aria'
              isInvalid={!isCouponValid}
              type='text'
              className={style.Field}
              isDisabled={isRewardsApplied}
            >
              <VisuallyHidden as={Label}>Coupon code</VisuallyHidden>
              <Input className={style.Input} placeholder='Coupon code' />
              {couponError && (
                <FieldError className={style.Error}>{couponError.message}</FieldError>
              )}
            </TextField>
            <PrimaryButton
              type='submit'
              size='sm'
              className={style.ApplyBtn}
              isPending={isCouponLoading}
            >
              Apply
            </PrimaryButton>
          </VisuallyHidden>
          {couponInfo && !isRewardsApplied && (
            <div className={style.CouponInfo}>
              <div>Coupon applied</div>
              <div>
                <Price
                  price={-couponInfo.amount}
                  options={{
                    fractionDigits: USD_DISPLAY_DECIMALS,
                    exactFractionDigits: true,
                  }}
                />
              </div>
            </div>
          )}
        </>
      )}
      <div className={style.Divider} />
      <div className={style.Total}>
        <span>Total</span>
        <span>
          <Price
            price={total}
            options={{
              fractionDigits: USD_DISPLAY_DECIMALS,
              exactFractionDigits: true,
            }}
          />
        </span>
      </div>
      {isOpen && cashback && (
        <div className={style.SumCategory}>
          <span className={clsx(style.SumCategoryName, style.GoodValue, style.Strong)}>
            <PriceHistorySvg className={style.Decoration} aria-hidden />
            Rewards ({cashback.rate.mul(100).toNumber()}%)
          </span>
          <span className={clsx(style.SumCategoryValue, style.GoodValue)}>
            <Price
              price={cashback.value}
              options={{
                fractionDigits: USD_DISPLAY_DECIMALS,
                exactFractionDigits: true,
              }}
            />
          </span>
        </div>
      )}
      <ExpressCheckout
        applyRewards={isRewardsApplied}
        className={style.ExpressCheckout}
        coupon={coupon}
      />
      <PrimaryButton
        className={style.PayBtn}
        onPress={onPress}
        isDisabled={cartItemNumber === 0}
        // isPending={isCheckingOut}
        aria-label='Pay with Card (Guaranteed safe & secure, powered by Stripe)'
      >
        {/* {!isCheckingOut && <img className={style.LockIcon} src={lockSvg} alt='' />}
        {!isCheckingOut ? 'Pay now' : 'Paying...'} */}
        Pay with Card
      </PrimaryButton>
      {isOpen && <img src={stripeTrustBadgePng} alt='' />}
    </div>
  )
})
